import type {
  ChatSessionResponse,
  UpdateChatSessionRequest,
} from '@automatick/shared-ng';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  ScopeOwnedAsyncRequestTracker,
  type ScopeOwnedAsyncRequest,
} from './scope-owned-async';
import { WebChatMessageRuntime } from './web-chat-message.runtime';
import { WebChatRealtimeRuntime } from './web-chat-realtime.runtime';
import {
  isExactWebChatSessionScope,
  retainExactWebChatSessions,
  WebChatSessionRuntime,
} from './web-chat-session.runtime';
import { WebChatStateStore } from './web-chat-state.store';
import { isCanonicalWebChatIdentifier } from './web-chat.models';

interface SessionRequestOwner {
  readonly request: ScopeOwnedAsyncRequest;
  readonly workspaceId: string;
  readonly bindingId: string;
  readonly sessionId: string;
}

type SessionUpdateEffect = 'upsert' | 'activate-project' | 'remove';

export class WebChatSessionCatalogRuntime {
  private readonly sessionSearchRequests = new ScopeOwnedAsyncRequestTracker();
  private readonly sessionHistoryRequests = new ScopeOwnedAsyncRequestTracker();
  private readonly messageSearchRequests = new ScopeOwnedAsyncRequestTracker();
  private readonly renameRequests = new ScopeOwnedAsyncRequestTracker();
  private readonly preferredAgentRequests = new ScopeOwnedAsyncRequestTracker();
  private readonly projectScopeRequests = new ScopeOwnedAsyncRequestTracker();
  private readonly archiveRequests = new ScopeOwnedAsyncRequestTracker();
  private readonly deleteRequests = new ScopeOwnedAsyncRequestTracker();
  private destroyed = false;

  constructor(
    private readonly state: WebChatStateStore,
    private readonly sessions: WebChatSessionRuntime,
    private readonly messages: WebChatMessageRuntime,
    private readonly realtime: WebChatRealtimeRuntime,
  ) {}

  destroy(): void {
    this.destroyed = true;
    this.sessionSearchRequests.invalidate();
    this.sessionHistoryRequests.invalidate();
    this.messageSearchRequests.invalidate();
    this.renameRequests.invalidate();
    this.preferredAgentRequests.invalidate();
    this.projectScopeRequests.invalidate();
    this.archiveRequests.invalidate();
    this.deleteRequests.invalidate();
  }

  searchSessions(search: string): void {
    const owner = this.beginRequest(
      this.sessionSearchRequests,
      this.state.stateSubject.value.binding?.id,
    );
    if (!owner) {
      return;
    }

    const normalizedSearch = search.trim();
    this.state.patch({
      isSearchingSessions: true,
      sessionSearchTerm: normalizedSearch,
    });
    this.state.chatClient
      .listSessions({
        query: {
          channelBindingId: owner.bindingId,
          take: 50,
          search: normalizedSearch || undefined,
          includeArchived: false,
        },
      })
      .pipe(
        finalize(() => {
          if (this.owns(this.sessionSearchRequests, owner)) {
            this.state.patch({ isSearchingSessions: false });
          }
        }),
        takeUntil(this.state.destroy$),
      )
      .subscribe({
        next: (sessions) => {
          if (!this.owns(this.sessionSearchRequests, owner)) {
            return;
          }

          const ownedSessions = retainExactWebChatSessions(
            sessions,
            owner.workspaceId,
            owner.bindingId,
          );
          const selectedSessionId =
            this.state.stateSubject.value.session?.sessionId ?? null;
          const selectedSession = selectedSessionId
            ? (ownedSessions.find(
                (session) => session.sessionId === selectedSessionId,
              ) ?? null)
            : null;
          this.state.patch({
            sessions: ownedSessions,
            session: selectedSession ?? this.state.stateSubject.value.session,
          });
        },
        error: () => void this.owns(this.sessionSearchRequests, owner),
      });
  }

  searchMessages(search: string): void {
    const owner = this.beginRequest(
      this.messageSearchRequests,
      this.state.stateSubject.value.session?.sessionId,
    );
    if (!owner || !this.ownsCurrentSession(this.messageSearchRequests, owner)) {
      return;
    }

    const normalizedSearch = search.trim();
    this.state.patch({
      isSearchingMessages: true,
      messageSearchTerm: normalizedSearch,
    });
    this.state.chatClient
      .listSessionMessages({
        sessionId: owner.sessionId,
        query: { take: 80, search: normalizedSearch || undefined },
      })
      .pipe(
        finalize(() => {
          if (this.ownsCurrentSession(this.messageSearchRequests, owner)) {
            this.state.patch({ isSearchingMessages: false });
          }
        }),
        takeUntil(this.state.destroy$),
      )
      .subscribe({
        next: (messages) => {
          if (this.ownsCurrentSession(this.messageSearchRequests, owner)) {
            this.state.patch({
              messages: this.messages.sortMessagesChronologically(
                [...messages].reverse(),
              ),
            });
          }
        },
        error: () =>
          void this.ownsCurrentSession(this.messageSearchRequests, owner),
      });
  }

  switchSession(sessionId: string): void {
    const { phase, session, sessions } = this.state.stateSubject.value;
    const target = sessions.find(
      (candidate) => candidate.sessionId === sessionId,
    );
    if (
      !isCanonicalWebChatIdentifier(sessionId) ||
      phase !== 'ready' ||
      session?.sessionId === sessionId ||
      !target
    ) {
      return;
    }

    const owner = this.beginRequest(this.sessionHistoryRequests, sessionId);
    if (
      !owner ||
      !isExactWebChatSessionScope(target, owner.workspaceId, owner.bindingId)
    ) {
      return;
    }

    this.projectScopeRequests.invalidate();
    this.realtime.resetLiveChannelState();
    this.state.patch({
      session: target,
      selectedProjectId: target.projectId ?? null,
      messages: [],
      sessionFiles: [],
      uxCapabilities: null,
      thoughtTraces: [],
      thoughtTraceEntriesById: {},
      loadingThoughtTraceIds: [],
      activeThoughtTraceId: null,
      finalRenderMessage: null,
      isThoughtChainActive: false,
      isLoadingHistory: true,
      isLoadingFiles: true,
    });
    this.sessions
      .loadCapabilitiesAndHistory(target)
      .pipe(
        finalize(() => {
          if (this.ownsCurrentSession(this.sessionHistoryRequests, owner)) {
            this.state.patch({
              isLoadingHistory: false,
              isLoadingFiles: false,
            });
          }
        }),
        takeUntil(this.state.destroy$),
      )
      .subscribe({
        next: (result) => {
          if (!this.ownsCurrentSession(this.sessionHistoryRequests, owner)) {
            return;
          }

          this.state.patch({
            uxCapabilities: result.capabilities,
            messages: result.messages,
            sessionFiles: result.sessionFiles,
            thoughtTraces: result.thoughtTraces,
            thoughtTraceEntriesById: {},
            loadingThoughtTraceIds: [],
            activeThoughtTraceId: null,
          });
          this.realtime.connectLive();
        },
        error: () =>
          void this.ownsCurrentSession(this.sessionHistoryRequests, owner),
      });
  }

  createNewSession(): void {
    if (this.state.stateSubject.value.phase !== 'ready') {
      return;
    }

    this.sessionHistoryRequests.invalidate();
    this.messageSearchRequests.invalidate();
    this.projectScopeRequests.invalidate();
    this.realtime.resetLiveChannelState();
    this.state.patch({
      session: null,
      uxCapabilities: null,
      messages: [],
      sessionFiles: [],
      isLoadingHistory: false,
      isLoadingFiles: false,
      latestRenderEvent: null,
      latestSearchRenderEvent: null,
      latestHealthCheckRenderEvent: null,
      recentRenderEvents: [],
      presentationRenderEvents: [],
      thoughtTraces: [],
      thoughtTraceEntriesById: {},
      loadingThoughtTraceIds: [],
    });
  }

  renameSession(sessionId: string, title: string): void {
    this.updateOwnedSession(
      this.renameRequests,
      sessionId,
      { title: title.trim() || null },
      'upsert',
    );
  }

  updateSessionPreferredPlatformAgent(
    sessionId: string,
    preferredPlatformAgentId: string | null,
  ): void {
    this.updateOwnedSession(
      this.preferredAgentRequests,
      sessionId,
      { preferredPlatformAgentId: preferredPlatformAgentId?.trim() || '' },
      'upsert',
    );
  }

  setProjectScope(projectId: string | null): void {
    const sessionId = this.state.stateSubject.value.session?.sessionId;
    const normalizedProjectId = canonicalInputIdentifier(projectId);
    if (!sessionId) {
      if (this.activeWorkspaceId) {
        this.state.patch({ selectedProjectId: normalizedProjectId });
      }
      return;
    }

    this.updateOwnedSession(
      this.projectScopeRequests,
      sessionId,
      normalizedProjectId
        ? { projectId: normalizedProjectId }
        : { clearProjectScope: true },
      'activate-project',
    );
  }

  archiveSession(sessionId: string): void {
    this.updateOwnedSession(
      this.archiveRequests,
      sessionId,
      { isArchived: true },
      'remove',
    );
  }

  deleteSession(sessionId: string): void {
    const owner = this.beginRequest(this.deleteRequests, sessionId);
    if (!owner) {
      return;
    }

    this.state.chatClient
      .deleteSession({ sessionId: owner.sessionId })
      .pipe(takeUntil(this.state.destroy$))
      .subscribe({
        next: () => {
          if (this.owns(this.deleteRequests, owner)) {
            this.removeSession(owner.sessionId);
          }
        },
        error: () => void this.owns(this.deleteRequests, owner),
      });
  }

  activateSession(session: ChatSessionResponse): void {
    const workspaceId = this.activeWorkspaceId;
    const bindingId = this.state.stateSubject.value.binding?.id;
    if (
      !workspaceId ||
      !isCanonicalWebChatIdentifier(bindingId) ||
      !isExactWebChatSessionScope(session, workspaceId, bindingId)
    ) {
      return;
    }

    this.sessionHistoryRequests.invalidate();
    this.messageSearchRequests.invalidate();
    this.projectScopeRequests.invalidate();
    this.messages.upsertSession(session, true);
    this.state.patch({ selectedProjectId: session.projectId ?? null });
    if (session.uxCapabilities) {
      this.state.patch({ uxCapabilities: session.uxCapabilities });
    }
    this.realtime.connectLive();
  }

  private updateOwnedSession(
    tracker: ScopeOwnedAsyncRequestTracker,
    sessionId: string,
    body: UpdateChatSessionRequest,
    effect: SessionUpdateEffect,
  ): void {
    const owner = this.beginRequest(tracker, sessionId);
    if (!owner) {
      return;
    }

    this.state.chatClient
      .updateSession({ sessionId: owner.sessionId, body })
      .pipe(
        finalize(() => void this.owns(tracker, owner)),
        takeUntil(this.state.destroy$),
      )
      .subscribe({
        next: (session) => {
          if (
            !this.ownsResponse(tracker, owner, session) ||
            (effect === 'activate-project' &&
              !this.ownsCurrentSession(tracker, owner))
          ) {
            return;
          }

          if (effect === 'remove') {
            this.removeSession(owner.sessionId);
            return;
          }
          this.messages.upsertSession(session, effect === 'activate-project');
          if (effect === 'activate-project') {
            this.state.patch({ selectedProjectId: session.projectId ?? null });
          }
        },
        error: () => void this.owns(tracker, owner),
      });
  }

  private beginRequest(
    tracker: ScopeOwnedAsyncRequestTracker,
    sessionId: string | null | undefined,
  ): SessionRequestOwner | null {
    const workspaceId = this.activeWorkspaceId;
    const bindingId = this.state.stateSubject.value.binding?.id;
    if (
      !workspaceId ||
      !isCanonicalWebChatIdentifier(bindingId) ||
      !isCanonicalWebChatIdentifier(sessionId)
    ) {
      return null;
    }

    return {
      request: tracker.begin(buildScopeKey(workspaceId, sessionId)),
      workspaceId,
      bindingId,
      sessionId,
    };
  }

  private ownsCurrentSession(
    tracker: ScopeOwnedAsyncRequestTracker,
    owner: SessionRequestOwner,
  ): boolean {
    return (
      this.state.stateSubject.value.session?.sessionId === owner.sessionId &&
      this.owns(tracker, owner)
    );
  }

  private owns(
    tracker: ScopeOwnedAsyncRequestTracker,
    owner: SessionRequestOwner,
  ): boolean {
    return (
      !this.destroyed &&
      this.activeWorkspaceId === owner.workspaceId &&
      this.state.stateSubject.value.binding?.id === owner.bindingId &&
      tracker.owns(
        owner.request,
        buildScopeKey(owner.workspaceId, owner.sessionId),
      )
    );
  }

  private ownsResponse(
    tracker: ScopeOwnedAsyncRequestTracker,
    owner: SessionRequestOwner,
    session: ChatSessionResponse,
  ): boolean {
    return (
      this.owns(tracker, owner) &&
      session.sessionId === owner.sessionId &&
      isExactWebChatSessionScope(session, owner.workspaceId, owner.bindingId)
    );
  }

  private removeSession(sessionId: string): void {
    const sessions = this.state.stateSubject.value.sessions.filter(
      (session) => session.sessionId !== sessionId,
    );
    if (this.state.stateSubject.value.session?.sessionId !== sessionId) {
      this.state.patch({ sessions });
      return;
    }

    this.sessionHistoryRequests.invalidate();
    this.messageSearchRequests.invalidate();
    this.projectScopeRequests.invalidate();
    this.realtime.resetLiveChannelState();
    this.state.patch({
      sessions,
      session: null,
      uxCapabilities: null,
      messages: [],
      latestRenderEvent: null,
      recentRenderEvents: [],
      presentationRenderEvents: [],
      thoughtTraces: [],
      thoughtTraceEntriesById: {},
      loadingThoughtTraceIds: [],
    });
  }

  private get activeWorkspaceId(): string | null {
    return canonicalInputIdentifier(
      this.state.appState.getStateSnapshot().activeWorkspaceId,
    );
  }
}

function canonicalInputIdentifier(
  value: string | null | undefined,
): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

function buildScopeKey(workspaceId: string, sessionId: string): string {
  return `${workspaceId}\u0000${sessionId}`;
}
